import { z } from 'zod';
import { dataHoraSchema, dataSchema } from './comum.ts';

/**
 * NCM — Nomenclatura Comum do Mercosul.
 *
 * Catálogo do governo, **igual para todas as contas**, guardado no banco para o
 * sistema não depender de API em tempo de uso: quem digita uma nota precisa da
 * descrição na hora, e uma consulta externa por item significaria a nota
 * travando toda vez que o Portal Único estiver fora do ar.
 *
 * Dois planos de acesso entram aqui, e a assimetria é a regra do módulo:
 *
 * - **Cliente** só lê (`cadastros.ncm.ver`).
 * - **Console de operação** lê e atualiza (`sistema.ncm.*`).
 *
 * Não existe schema de escrita para o plano de cliente, e a ausência é
 * deliberada: o catálogo é o texto da lei, não um cadastro da contratante.
 */

/**
 * Código NCM **só com dígitos**, de 2 a 8.
 *
 * Oito dígitos é o item completo, o que vai na nota fiscal. Os menores são os
 * níveis da hierarquia — capítulo, posição, subposição —, que existem para dar
 * sentido às descrições.
 */
export const codigoNcmSchema = z
  .string()
  .regex(/^\d{2,8}$/, 'O código NCM tem de 2 a 8 dígitos, sem pontos.');

export const ncmSchema = z.object({
  codigo: codigoNcmSchema,
  /** Descrição oficial, como o governo publica — inclusive os traços de nível. */
  descricao: z.string(),
  /**
   * A descrição com o caminho da hierarquia à frente.
   *
   * É o campo que as telas usam. "-- Outros" sozinho não informa nada; "Animais
   * vivos › Cavalos, asininos e muares, vivos › Cavalos › Outros" informa.
   */
  descricaoCompleta: z.string(),
  nivel: z.number().int(),
  dataInicio: dataSchema.nullable(),
  dataFim: dataSchema.nullable(),
  atoLegal: z.string().nullable(),
});

export type Ncm = z.infer<typeof ncmSchema>;

/**
 * Consulta do catálogo pelo cliente.
 *
 * Sem paginação por página e sim por `limit`, como o catálogo de municípios:
 * isto alimenta autocompletar, não uma tela de listagem — quem consulta digita
 * mais um caractere em vez de virar a página.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const listarNcmsQuerySchema = z
  .object({
    /** Busca por código ou por trecho da descrição completa. */
    q: z.string().trim().min(1).max(200).optional(),
    /**
     * Só os códigos de oito dígitos, que é o que a nota fiscal aceita.
     *
     * Padrão **verdadeiro**: oferecer capítulos e posições num campo de NCM de
     * produto produziria documento fiscal recusado.
     */
    somenteCompletos: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .transform((v) => v !== false && v !== 'false')
      .prefault(true),
    limit: z.coerce.number().int().min(1).max(100).prefault(20),
  })
  .strict();

export type ListarNcmsQuery = z.infer<typeof listarNcmsQuerySchema>;

export const listaNcmsSchema = z.object({ data: z.array(ncmSchema) });
export type ListaNcms = z.infer<typeof listaNcmsSchema>;

/* -------------------------------------------------------------------------- */
/* Console de operação                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Estado do catálogo.
 *
 * `importado` distingue **"nunca foi importado"** de "importado e deu zero" — o
 * primeiro é o estado normal de instalação nova, o segundo é defeito, e a tela
 * precisa dizer coisas diferentes nos dois casos.
 */
export const statusCatalogoNcmSchema = z.object({
  importado: z.boolean(),
  total: z.number().int(),
  /** Vigência declarada pelo arquivo, como "Vigente em 08/08/2026". */
  vigenteEm: z.string().nullable(),
  ato: z.string().nullable(),
  atualizadoEm: dataHoraSchema.nullable(),
  /** Últimas importações, da mais recente para a mais antiga. */
  historico: z.array(z.object({ detalhe: z.string(), em: dataHoraSchema })),
});

export type StatusCatalogoNcm = z.infer<typeof statusCatalogoNcmSchema>;

export const resultadoAtualizacaoNcmSchema = z.object({
  total: z.number().int(),
  vigenteEm: z.string(),
  ato: z.string(),
});

export type ResultadoAtualizacaoNcm = z.infer<typeof resultadoAtualizacaoNcmSchema>;
